/* eslint-disable eslint-comments/no-unlimited-disable */
/* eslint-disable */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _isEqual from 'lodash/isEqual';
import { useSelector, useDispatch } from 'react-redux';
import { focusManager } from '@accedo/vdkweb-navigation';
import cx from 'classnames';
import moment from 'moment-mini';

import { getDeepLinkStarted } from '#/redux/modules/lifecycle/selectors';
import { heroBannerEnabled } from '#/redux/modules/accedoOne/selectors';
import { getCurrentProfile } from '#/redux/modules/onboarding/selectors';
import { getLoadingSpinnerState } from '#/redux/modules/spinner/selectors';
import { clearVideoAndBookmark } from '#/redux/modules/appPlayer/actions';
import {
  loadAllShows,
  loadTopShows,
  loadAllTopics,
  loadHeroContent,
  clearHeroContent,
} from '#/redux/modules/firestore/actions';
import { actions as modalActions } from '#/redux/modules/modal/actions';

import {
  loadCustomDataShowsFavorites,
  loadCustomDataRecentlyWatched,
  loadCustomDataTopicsFavorites,
  loadCustomDataVideoFavorites,
  setGuestMode,
  clearCustomDataRecentlyWatched,
  loadCustomDataDisplayedModals,
} from '#/redux/modules/identity/actions';
import {
  setGuestMode as setGuetsModeSelectors,
  getCustomDataVideosBlocked,
  getCustomDataShowsBlocked,
} from '#/redux/modules/identity/selectors';

import { isWorkstation, isVIZIO, isTizen } from '#/utils/componentStyleConfig';
import useVizioLibrary from '#/hooks/useVizioLibrary';
import SplashScreen from '#/components/AuthFlow/SplashScreen/SplashScreen';
import LoadingSpinner from '#/components/LoadingSpinner/LoadingSpinner';
import FocusSink from '#/components/FocusSink/FocusSink';

import { isGuestMode } from '#/redux/modules/user/selectors';
import { getAgeRange } from '#/utils/getAgeRange';
import { aggregateBirthdate } from '#/utils/aggregateBirthdate';
import storage from '#/utils/localStorage';
import { useLocalStorage } from '#/hooks/useLocalStorage';
import {
  getCurrentAgeMetadata,
  getEntryById,
} from '#/redux/modules/accedoOne/selectors';

import styles from './tvApp.scss';
import useLatest from '#/hooks/useLatest';
import { getReloadApplication } from '#/redux/modules/menu/selectors';
import { rerenderApplication } from '#/redux/modules/menu/actions';

const initialWelcomeData = (profileId, ageRange) => {
  if (!profileId) {
    return;
  }
  const data = JSON.parse(storage.get('welcomeModal')) || {};

  if (profileId === 'UNKNOWN') {
    const updatedData = data;

    if (!('guest' in data)) {
      updatedData.guest = {};
    }
    updatedData.guest[`${ageRange}`] = {
      showWelcomeModal: true,
    };

    return updatedData;
  }

  if (!(profileId in data)) {
    const updatedData = data;
    updatedData[`${profileId}`] = {
      showWelcomeModal: true,
    };

    return updatedData;
  }

  return data;
};

const TVAppContent = ({ children }) => {
  focusManager.setPersistTrail(true);
  const dispatch = useDispatch();
  const getTriggerAppReloadMode = useSelector(getReloadApplication);
  const currentProfile = useSelector(getCurrentProfile);
  const isHeroBannerEnabled = useSelector(heroBannerEnabled, _isEqual);
  const loadingSpinnerState = useSelector(getLoadingSpinnerState);
  const guestMode = useSelector(isGuestMode);
  const ageRange = getAgeRange(currentProfile?.age);
  const customDataShowsBlocked = useSelector(getCustomDataShowsBlocked);
  const customDataVideosBlocked = useSelector(getCustomDataVideosBlocked);
  const selectedProfileId = currentProfile?.id;
  const selectedAgeMetadata = useSelector(getCurrentAgeMetadata);
  const deeplinkLaunch = useSelector(getDeepLinkStarted);
  const deeplinkLaunchRef = useLatest(deeplinkLaunch);
  const welcomeModalEntryData = useSelector(state =>
    getEntryById(state, selectedAgeMetadata?.welcomeModals),
  );
  const [showWelcomeModal, setShowWelcomeModal] = useLocalStorage(
    'welcomeModal',
    initialWelcomeData(selectedProfileId, selectedAgeMetadata?.ageGroupValue),
  );
  const [trigger, setTrigger] = useState(false);
  const showWelcomeModalRef = useLatest(showWelcomeModal);

  if (isVIZIO()) {
    useVizioLibrary();
  }

  if (isTizen()) {
    let prevFocus = '';

    const saveState = () => {
      prevFocus = focusManager.getCurrentFocus();
    };

    const restoreState = () => {
      if (prevFocus) {
        // Set focus on next frame to override xdk's default focus behaviour
        setTimeout(() => {
          focusManager.changeFocus(prevFocus);
        }, 0);
      }
    };

    // This will be called when the app gets minimized
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        restoreState();
      } else {
        saveState();
      }
    });

    // On new Tizen devices where multitasking is available, these events get called
    window.addEventListener('focus', () => {
      restoreState();
    });

    window.addEventListener('blur', () => {
      saveState();
    });
  }

  useEffect(() => {
    setGuestMode(guestMode);
    setGuetsModeSelectors(guestMode);
  }, [guestMode]);

  useEffect(() => {
    if (!currentProfile?.age) {
      return () => {};
    }

    dispatch(clearCustomDataRecentlyWatched());
    dispatch(clearVideoAndBookmark());
    dispatch(loadAllShows());
    dispatch(loadTopShows());
    dispatch(loadAllTopics());
    dispatch(loadCustomDataShowsFavorites());
    dispatch(loadCustomDataRecentlyWatched());
    // dispatch(loadCustomDataPodcastsFavorites());
    dispatch(loadCustomDataTopicsFavorites());
    dispatch(loadCustomDataVideoFavorites());

    if (isHeroBannerEnabled.enabled) {
      dispatch(loadHeroContent(isHeroBannerEnabled.collection));
    } else {
      dispatch(clearHeroContent());
    }

    // Don't show modals if doing a deeplink
    if (deeplinkLaunchRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      const birthDate = aggregateBirthdate(
        currentProfile.age,
        currentProfile.birthdayMonth,
      );

      const currentMonthDay15 = moment().date(15);
      const day15OfMonth = moment()
        .date(15)
        .format('YYYY-MM-DD');
      const yearDiff = moment(day15OfMonth, 'YYYY-MM-DD').diff(
        moment(birthDate, 'YYYY-MM-DD'),
        'years',
      );

      const kidAgeUpDuringBirthMonth =
        currentProfile.birthdayMonth === moment().month() + 1 &&
        moment().isAfter(currentMonthDay15) &&
        (yearDiff === 5 || yearDiff === 8);
      const kidAgeUpAfterBirthMonth =
        moment().isAfter(currentMonthDay15) &&
        currentProfile.birthdayMonth !== moment().month() + 1 &&
        (yearDiff !== 5 || yearDiff !== 8);

      if (!guestMode && ageRange > 2) {
        dispatch(loadCustomDataDisplayedModals());
        if (
          !currentProfile?.displayedModals ||
          !currentProfile.displayedModals.some(
            obj => obj.id === `ageUp_${ageRange}up`,
          )
        ) {
          if (
            (kidAgeUpDuringBirthMonth || kidAgeUpAfterBirthMonth) &&
            window.location.pathname === '/main/home'
          ) {
            dispatch(
              modalActions.openModal({
                modalId: 'AgeUpModal',
                modalData: {
                  ageGroupValue: ageRange,
                  kidAgeUpDuringBirthMonth,
                  kidAgeUpAfterBirthMonth,
                },
              }),
            );
          }
        }
      }

      if (guestMode) {
        const getWelcomeModalData =
          showWelcomeModalRef.current?.guest?.[
            `${selectedAgeMetadata.ageGroupValue}`
          ]?.showWelcomeModal;

        if (
          (getWelcomeModalData || getWelcomeModalData === undefined) &&
          selectedAgeMetadata.welcomeModals &&
          welcomeModalEntryData &&
          window.location.pathname === '/main/home'
        ) {
          dispatch(
            modalActions.openModal({
              modalId: 'WelcomeModal',
              modalData: {
                modalId: selectedAgeMetadata.welcomeModals,
              },
            }),
          );

          const newWelcomeModalData = showWelcomeModalRef.current || {};

          if (!('guest' in newWelcomeModalData)) {
            newWelcomeModalData.guest = {};
          }

          newWelcomeModalData.guest[`${selectedAgeMetadata.ageGroupValue}`] = {
            showWelcomeModal: false,
          };
          setShowWelcomeModal(newWelcomeModalData);
        }
      } else {
        const getWelcomeModalData =
          showWelcomeModalRef.current?.[`${selectedProfileId}`]
            ?.showWelcomeModal;

        if (
          (getWelcomeModalData || getWelcomeModalData === undefined) &&
          selectedAgeMetadata.welcomeModals &&
          welcomeModalEntryData &&
          window.location.pathname === '/main/home'
        ) {
          dispatch(
            modalActions.openModal({
              modalId: 'WelcomeModal',
              modalData: {
                modalId: selectedAgeMetadata.welcomeModals,
              },
            }),
          );

          const newWelcomeModalData = showWelcomeModalRef.current || {};
          newWelcomeModalData[`${selectedProfileId}`] = {
            showWelcomeModal: false,
          };
          setShowWelcomeModal(newWelcomeModalData);
        }
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [
    currentProfile?.id,
    isHeroBannerEnabled.enabled,
    customDataShowsBlocked,
    customDataVideosBlocked,
  ]);
  useEffect(() => {
    if (getTriggerAppReloadMode) {
      setTrigger(true);
      dispatch(modalActions.closeModal());
      dispatch(rerenderApplication(false));
    } else {
      setTrigger(false);
    }
  }, [getTriggerAppReloadMode]);
  return (
    <>
      {!trigger ? (
        <div
          className={cx(styles.root, {
            [styles.tv]: !isWorkstation(),
          })}
        >
          <FocusSink />

          {!isWorkstation() && <SplashScreen />}
          {children}
          <LoadingSpinner showLoading={loadingSpinnerState} />
        </div>
      ) : (
        <LoadingSpinner showLoading={true} />
      )}
    </>
  );
};

TVAppContent.propTypes = {
  children: PropTypes.node,
};

export default TVAppContent;
